/**
 * Ticket numbers — the short, sayable reference on every service request
 * ("SR-0042") and fault ("FLT-0017"), so the office can name a ticket in
 * Slack or on the phone instead of "Sarah's NBN one, no, the other one".
 *
 * One counter doc per tenant at `tenants/{tenantId}/counters/ticket_number`
 * holds one sequence per kind (`{ service_request: 42, fault: 17 }`). The
 * counter bump and the ticket create happen in ONE transaction, so numbers
 * are unique under concurrency and a failed create burns nothing.
 *
 * Shared by the client (src/services/ticket-number.service.ts), the Claude
 * connector's fault create (functions/src/mcp/faults.ts) and the backfill
 * script (src/scripts/backfill-ticket-numbers.ts) — all three must agree on
 * the doc, the field and the format, so they all read them from here.
 */

export type TicketKind = 'service_request' | 'fault';

export const TICKET_PREFIX: Record<TicketKind, string> = {
  service_request: 'SR',
  fault: 'FLT',
};

/** Doc id under `tenants/{tenantId}/counters/`. */
export const TICKET_COUNTER_DOC = 'ticket_number';

const PAD_LENGTH = 4;

/** Path segments of the tenant's ticket counter doc. */
export function ticketCounterPath(tenantId: string): [string, string, string, string] {
  return ['tenants', tenantId, 'counters', TICKET_COUNTER_DOC];
}

/** `formatTicketNumber('fault', 7)` → `'FLT-0007'`; grows past 9999 unpadded. */
export function formatTicketNumber(kind: TicketKind, seq: number): string {
  return `${TICKET_PREFIX[kind]}-${String(seq).padStart(PAD_LENGTH, '0')}`;
}

/** Last sequence used for `kind`, read from the counter doc's data (0 if none). */
export function currentTicketSeq(
  counter: Record<string, unknown> | undefined,
  kind: TicketKind,
): number {
  const v = counter?.[kind];
  return typeof v === 'number' && Number.isFinite(v) && v > 0 ? Math.floor(v) : 0;
}

/** Minimal shape the backfill needs from a ticket doc. */
export interface BackfillTicket {
  id: string;
  ticketNumber?: string;
  intakeDate?: string;
  createdAt?: string;
}

export interface BackfillAssignment {
  id: string;
  ticketNumber: string;
}

function sortKey(t: BackfillTicket): string {
  return (typeof t.intakeDate === 'string' && t.intakeDate) ||
    (typeof t.createdAt === 'string' && t.createdAt) ||
    '';
}

/**
 * Work out the numbers to give tickets that don't have one yet: oldest first
 * (`intakeDate`, falling back to `createdAt`, then doc id for a stable
 * order), continuing from `lastSeq`. Tickets that already carry a number are
 * left alone, so running it twice assigns nothing the second time.
 */
export function planTicketBackfill(
  kind: TicketKind,
  tickets: BackfillTicket[],
  lastSeq: number,
): BackfillAssignment[] {
  const missing = tickets
    .filter((t) => !t.ticketNumber)
    .sort((a, b) => {
      const ka = sortKey(a);
      const kb = sortKey(b);
      if (ka !== kb) {
        // Undated tickets go last.
        if (!ka) return 1;
        if (!kb) return -1;
        return ka < kb ? -1 : 1;
      }
      return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
    });
  return missing.map((t, i) => ({
    id: t.id,
    ticketNumber: formatTicketNumber(kind, lastSeq + i + 1),
  }));
}
